import type { ReactNode } from 'react';
import { cx } from '../lib/cx.js';
import type { Platform } from '@dintiko/shared';
import { PlatformLogo } from './platform-logo.js';

/**
 * Kept as the name every screen already imports; the drawing lives in
 * `platform-logo.tsx` so there is one implementation, not two that drift.
 */
export function PlatformMark({ platform, size = 16 }: { platform: Platform; size?: number }) {
  return <PlatformLogo platform={platform} size={size} />;
}

const platformLabel: Record<Platform, string> = {
  youtube: 'YouTube',
  instagram: 'Instagram',
  tiktok: 'TikTok',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  telegram: 'Telegram',
  mock: 'Mock',
};

export interface AccountChipProps {
  platform: Platform;
  handle: string;
  selected?: boolean;
  /** Set when the account cannot be posted to — an expired login, say. */
  unavailableReason?: string;
  onToggle?: () => void;
}

/**
 * One connected handle. Six of these is the normal case, so the chip stays
 * tight — but an account that cannot be used says why rather than silently
 * refusing the tap (PLAN §13.3).
 */
export function AccountChip({
  platform,
  handle,
  selected = false,
  unavailableReason,
  onToggle,
}: AccountChipProps) {
  const unavailable = Boolean(unavailableReason);
  const label = platformLabel[platform];

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={selected}
      aria-disabled={unavailable || undefined}
      onClick={unavailable ? undefined : onToggle}
      title={unavailableReason}
      className={cx(
        'aw-press inline-flex min-h-[36px] items-center gap-2 rounded-aw border px-2.5 py-1',
        'text-sm font-medium transition-colors',
        selected
          ? 'border-accent bg-accent-soft text-accent'
          : 'border-control bg-surface text-ink hover:bg-raise',
        unavailable && 'cursor-not-allowed border-line text-faint opacity-70',
      )}
    >
      {/* A tick box, so an unselected chip looks like something you can turn
          on rather than like a label. Selection was carried by border and
          background colour alone, which reads as styling, not as state. */}
      <span
        aria-hidden="true"
        className={cx(
          'grid h-4 w-4 shrink-0 place-items-center rounded-[3px] border text-2xs leading-none',
          selected ? 'border-accent bg-accent text-on-accent' : 'border-control bg-surface',
        )}
      >
        {selected ? '✓' : ''}
      </span>
      <PlatformMark platform={platform} />
      <span className="font-mono">{handle}</span>
      <span className="sr-only">
        {label}
        {unavailable ? `, unavailable: ${unavailableReason}` : selected ? ', selected' : ''}
      </span>
      {unavailable && (
        <span aria-hidden="true" className="text-bad">
          ✕
        </span>
      )}
    </button>
  );
}

export interface TagProps {
  children: ReactNode;
  /** Trimmed tags are shown struck through rather than removed, so the owner sees what was dropped. */
  dropped?: boolean;
  onRemove?: () => void;
}

export function Tag({ children, dropped = false, onRemove }: TagProps) {
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1 rounded-aw px-1.5 py-0.5 font-mono text-xs',
        dropped ? 'bg-raise text-faint line-through' : 'bg-accent-soft text-accent',
      )}
    >
      {children}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${String(children)}`}
          className="opacity-70 hover:opacity-100"
        >
          ✕
        </button>
      )}
    </span>
  );
}
